import { normalizeAlias } from '@atlassian/jira-project-settings-apps-common/src/fields.tsx';
import platformTransformers from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/services/transformer/platform-transformers.tsx';
import {
	type ActiveLayoutItem,
	type FieldLayoutItemOperations,
	FIELD_LAYOUT_ITEM_TYPE,
} from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/types/layout-items.tsx';
import {
	JSD_FIELD_APPROVAL_ALL_CONFIGS,
	JSD_FIELD_APPROVAL_CONFIG,
} from '@atlassian/jira-project-settings-product-features-jsm-common/src/common/constants.tsx';
import { JSD_FIELD_ALIAS, JSD_FIELD_HELPTEXT } from '../../constants.tsx';

type Overrides = {
	required: boolean;
	sortable: boolean;
	operations?: Partial<FieldLayoutItemOperations>;
};

const overridesForEmailRequestType: Record<string, Overrides> = {
	summary: {
		required: true,
		sortable: false,
	},
	description: {
		required: false,
		sortable: false,
		operations: {
			removable: false,
			canModifyRequired: false,
		},
	},
	attachment: {
		required: false,
		sortable: false,
		operations: {
			removable: false,
			canModifyRequired: false,
		},
	},
};

const applyOverridesForEmailRequestType = (item: ActiveLayoutItem): ActiveLayoutItem => {
	if (item.layoutItemType !== FIELD_LAYOUT_ITEM_TYPE) {
		return item;
	}

	const overrides = overridesForEmailRequestType[item.data.key];
	if (!overrides) {
		return item;
	}

	return {
		...item,
		data: {
			...item.data,
			required: overrides.required,
			sortable: overrides.sortable,
			operations: {
				...item.operations,
				...overrides.operations,
			},
		},
	};
};

export const jsmTmpTransformActiveLayoutItem: typeof platformTransformers.activeLayoutItem = (
	field,
	containerType,
	transformers,
) => {
	// first call the platform transformer
	const activeLayoutItem = platformTransformers.activeLayoutItem(
		field,
		containerType,
		transformers,
	);

	if (activeLayoutItem === null) {
		return null;
	}

	return {
		...activeLayoutItem,
		data: {
			...activeLayoutItem.data,
			...(activeLayoutItem.layoutItemType === FIELD_LAYOUT_ITEM_TYPE &&
				field.__typename === 'JiraIssueLayoutFieldItemConfiguration' &&
				field.properties && {
					// add the portal field name
					alternateName: normalizeAlias(field.properties[JSD_FIELD_ALIAS]),
					alternateDescription: normalizeAlias(field.properties[JSD_FIELD_HELPTEXT]),

					// The approval field configuration is currently saved in the fields properties,
					// For the purposes of this app, we want to read the approval config data via the fields configuration
					configuration: {
						...activeLayoutItem.data.configuration,
						...(field.properties[JSD_FIELD_APPROVAL_ALL_CONFIGS]
							? {
									[JSD_FIELD_APPROVAL_ALL_CONFIGS]:
										field.properties[JSD_FIELD_APPROVAL_ALL_CONFIGS],
								}
							: {}),
						...(field.properties[JSD_FIELD_APPROVAL_CONFIG]
							? {
									[JSD_FIELD_APPROVAL_CONFIG]: field.properties[JSD_FIELD_APPROVAL_CONFIG],
								}
							: {}),
					},
				}),
		},
	};
};

export const jsmTmpTransformActiveLayoutItemForEmailRequest: typeof platformTransformers.activeLayoutItem =
	(field, containerType, transformers) => {
		const item = jsmTmpTransformActiveLayoutItem(field, containerType, transformers);
		if (!item) {
			return item;
		}
		return applyOverridesForEmailRequestType(item);
	};
